import type { VisualField } from '../../../types/wizardTypes';
import { Trash2, GripVertical, ChevronDown, ChevronUp, Check, X, ImagePlus, ImageOff } from 'lucide-react';
import { useState, useEffect, useRef, type DragEvent, type ChangeEvent } from 'react';
import EnumValueSelect from '../../../ui/EnumValueSelect/EnumValueSelect';
import { getFieldKind } from '../converters/fieldTypeKind';
import styles from './FieldCard.module.css';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface FieldCardProps {
  field: VisualField;
  index: number;
  canEdit: boolean;
  isMssqlConnector?: boolean;
  onChange: (updates: Partial<VisualField>) => void;
  onRemove: () => void;
  onDragStart: (e: DragEvent<HTMLDivElement>) => void;
  onDragOver: (e: DragEvent<HTMLDivElement>) => void;
  onDragEnd: () => void;
  isDragOver: boolean;
  expandSignal?: { expand: boolean; v: number };
}

const FIELD_TYPES = [
  { value: 'string', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'integer', label: 'Integer' },
  { value: 'boolean', label: 'Yes / No' },
  { value: 'enum', label: 'Select (List)' },
  { value: 'date', label: 'Date' },
  { value: 'image', label: 'Image' },
] as const;

const SQL_FIELD_TYPES = [
  { value: 'string', label: 'string' },
  { value: 'int', label: 'int' },
  { value: 'varchar', label: 'varchar' },
  { value: 'nvarchar', label: 'nvarchar' },
  { value: 'text', label: 'text' },
  { value: 'bigint', label: 'bigint' },
  { value: 'smallint', label: 'smallint' },
  { value: 'tinyint', label: 'tinyint' },
  { value: 'decimal', label: 'decimal' },
  { value: 'float', label: 'float' },
  { value: 'bit', label: 'bit' },
  { value: 'date', label: 'date' },
  { value: 'datetime', label: 'datetime' },
  { value: 'datetime2', label: 'datetime2' },
  { value: 'uniqueidentifier', label: 'uniqueidentifier' },
] as const;

export default function FieldCard({
  field,
  index,
  canEdit,
  isMssqlConnector,
  onChange,
  onRemove,
  onDragStart,
  onDragOver,
  onDragEnd,
  isDragOver,
  expandSignal,
}: FieldCardProps) {
  const typeOptions = isMssqlConnector ? SQL_FIELD_TYPES : FIELD_TYPES;
  const fieldKind = getFieldKind(field.type);
  const [expanded, setExpanded] = useState(!field.name);
  const [newEnumKey, setNewEnumKey] = useState('');
  const [newEnumValue, setNewEnumValue] = useState('');
  const [imagePreview, setImagePreview] = useState<{ url: string; name: string; size: number } | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (expandSignal !== undefined) setExpanded(expandSignal.expand);
  }, [expandSignal]);

  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview.url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagePreview?.url]);

  const handleImageSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (imagePreview) URL.revokeObjectURL(imagePreview.url);
    setImagePreview({ url: URL.createObjectURL(file), name: file.name, size: file.size });
    onChange({ example: file.name });
    e.target.value = '';
  };

  const clearImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview.url);
    setImagePreview(null);
    onChange({ example: '' });
  };

  const isEnumType = field.type === 'enum';

  const addEnumValue = () => {
    const value = newEnumValue.trim();
    if (!value) return;
    const key = newEnumKey.trim() || value;
    const current = field.enumOptions || [];
    if (!current.some(o => o.value === value)) {
      onChange({ enumOptions: [...current, { key, value }] });
    }
    setNewEnumKey('');
    setNewEnumValue('');
  };

  const removeEnumValue = (value: string) => {
    onChange({ enumOptions: (field.enumOptions || []).filter(o => o.value !== value) });
  };

  return (
    <div
      className={`${styles.card} ${isDragOver ? styles.cardDragOver : ''}`}
      onDragOver={onDragOver}
    >
      <div className={styles.cardHeader}>
        <div
          className={styles.cardLeft}
          draggable={canEdit}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        >
          {canEdit && (
            <span className={styles.gripBtn} aria-label="Drag to reorder">
              <GripVertical size={14} />
            </span>
          )}
          <span className={styles.fieldIndex}>{index + 1}</span>
          <button
            type="button"
            className={styles.expandBtn}
            onClick={() => setExpanded(!expanded)}
            aria-label={expanded ? 'Collapse field' : 'Expand field'}
          >
            <span className={styles.fieldSummary}>
              {field.label || field.name || 'New Field'}
            </span>
            {/* {field.name && (
              <span className={styles.fieldTechName}>{field.name}</span>
            )} */}
            {field.required && <span className={styles.requiredBadge}>Required</span>}
            <span className={styles.typeBadge}>
              {typeOptions.find(t => t.value === field.type)?.label || field.type}
            </span>
          </button>
        </div>
        <div className={styles.cardActions}>
          <button type="button" className={styles.iconBtn} onClick={() => setExpanded(!expanded)} aria-label="Move up">
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {canEdit && (
            <button type="button" className={styles.deleteBtn} onClick={onRemove} aria-label="Remove field">
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className={styles.cardBody}>
          <div className={styles.fieldRow}>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Display Label</label>
              <input
                type="text"
                className={styles.fieldInput}
                value={field.label}
                onChange={e => {
                  const label = e.target.value;
                  const updates: Partial<VisualField> = { label };
                  if (!field.nameIsCustom && (!field.name || field.name === labelToName(field.label))) {
                    updates.name = labelToName(label);
                  }
                  onChange(updates);
                }}
                placeholder="e.g. Supplier Number"
                disabled={!canEdit}
              />
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Technical Name</label>
              <input
                type="text"
                className={styles.fieldInput}
                value={field.name}
                onChange={e => onChange({ name: e.target.value.replace(/[^a-zA-Z0-9_]/g, ''), nameIsCustom: true })}
                placeholder="e.g. LIFNR"
                disabled={!canEdit}
              />
            </div>
          </div>

          <div className={styles.fieldRow}>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Type</label>
              <select
                className={styles.fieldSelect}
                value={field.type}
                onChange={e => onChange({ type: e.target.value as VisualField['type'], example: '' })}
                disabled={!canEdit}
              >
                {typeOptions.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Example Value</label>
              {fieldKind === 'boolean' ? (
                <select
                  className={styles.fieldSelect}
                  value={field.example}
                  onChange={e => onChange({ example: e.target.value })}
                  disabled={!canEdit}
                >
                  <option value="">- Select -</option>
                  <option value="true">true</option>
                  <option value="false">false</option>
                </select>
              ) : field.type === 'enum' && field.enumOptions && field.enumOptions.length > 0 ? (
                <EnumValueSelect
                  options={field.enumOptions}
                  value={field.example}
                  onChange={v => onChange({ example: v })}
                  clearLabel="- Select -"
                  disabled={!canEdit}
                />
              ) :
                fieldKind === 'date' || fieldKind === 'datetime' ? (
                  <input
                    type={fieldKind === 'datetime' ? 'datetime-local' : 'date'}
                    step={fieldKind === 'datetime' ? 1 : undefined}
                    className={styles.fieldInput}
                    value={field.example}
                    onChange={e => onChange({ example: e.target.value })}
                    disabled={!canEdit}
                  />
                )
                  :
                  fieldKind === 'image' ? (
                    <div className={styles.imagePicker}>
                      <input
                        ref={imageInputRef}
                        type="file"
                        accept="image/*"
                        className={styles.imageInputHidden}
                        onChange={handleImageSelect}
                        disabled={!canEdit}
                      />
                      {imagePreview || field.example ? (
                        <div className={styles.imagePreview}>
                          <div className={styles.imageThumbWrap}>
                            <button
                              type="button"
                              className={styles.imageThumb}
                              onClick={() => canEdit && imageInputRef.current?.click()}
                              aria-label="Replace image"
                              disabled={!canEdit}
                            >
                              {imagePreview ? (
                                <img src={imagePreview.url} alt={imagePreview.name} />
                              ) : (
                                <ImageOff size={14} className={styles.imageThumbPlaceholderIcon} />
                              )}
                            </button>
                            {canEdit && (
                              <span
                                role="button"
                                tabIndex={0}
                                className={styles.imageRemoveBtn}
                                onClick={clearImage}
                                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); clearImage(); } }}
                                aria-label="Remove image"
                              >
                                <X size={10} />
                              </span>
                            )}
                          </div>
                          <div className={styles.imageMeta}>
                            <span className={styles.imageMetaName}>{imagePreview?.name || field.example}</span>
                            {imagePreview && (
                              <span className={styles.imageMetaSize}>{formatFileSize(imagePreview.size)}</span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className={styles.imagePickBtn}
                          onClick={() => imageInputRef.current?.click()}
                          disabled={!canEdit}
                        >
                          <ImagePlus size={14} />
                          Choose Image
                        </button>
                      )}
                    </div>
                  ) : (
                    <input
                      type={fieldKind === 'number' || fieldKind === 'integer' ? 'number' : 'text'}
                      className={styles.fieldInput}
                      value={field.example}
                      onChange={e => onChange({ example: e.target.value })}
                      step={fieldKind === 'integer' ? '1' : undefined}
                      placeholder={fieldKind === 'number' ? 'e.g. 3.14' : fieldKind === 'integer' ? 'e.g. 42' : 'e.g. 100234'}
                      disabled={!canEdit}
                    />
                  )}
            </div>
          </div>

          {isEnumType && (
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>List Options</label>
              <div className={styles.enumList}>
                <div className={styles.enumListHeader}>
                  <span className={styles.enumColKey}>Key <span className={styles.optionalBadge}>optional</span></span>
                  <span className={styles.enumColValue}>Value</span>
                </div>
                {(field.enumOptions || []).map(opt => (
                  <div key={opt.value} className={styles.enumRow}>
                    <span className={styles.enumRowKey}>
                      {opt.key !== opt.value ? opt.key : <span className={styles.enumRowKeyEmpty}>&mdash;</span>}
                    </span>
                    <span className={styles.enumRowValue}>{opt.value}</span>
                    {canEdit && (
                      <button
                        type="button"
                        className={styles.enumRowRemove}
                        onClick={() => removeEnumValue(opt.value)}
                        aria-label={`Remove ${opt.value}`}
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>
                ))}
                {canEdit && (
                  <div className={`${styles.enumRow} ${styles.enumRowNew}`}>
                    <input
                      type="text"
                      className={styles.enumRowInput}
                      value={newEnumKey}
                      onChange={e => setNewEnumKey(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEnumValue(); } }}
                      placeholder="Key (optional)"
                    />
                    <input
                      type="text"
                      className={styles.enumRowInput}
                      value={newEnumValue}
                      onChange={e => setNewEnumValue(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEnumValue(); } }}
                      placeholder="Value"
                    />
                    <button
                      type="button"
                      className={styles.enumRowAdd}
                      onClick={addEnumValue}
                      disabled={!newEnumValue.trim()}
                      aria-label="Add option"
                    >
                      <Check size={12} color="#000000" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className={styles.fieldRow}>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Default Value <span className={styles.optionalBadge}>optional</span></label>
              {fieldKind === 'boolean' ? (
                <select
                  className={styles.fieldSelect}
                  value={field.defaultValue ?? ''}
                  onChange={e => onChange({ defaultValue: e.target.value || undefined })}
                  disabled={!canEdit}
                >
                  <option value="">- None -</option>
                  <option value="true">true</option>
                  <option value="false">false</option>
                </select>
              ) : field.type === 'enum' && field.enumOptions && field.enumOptions.length > 0 ? (
                <EnumValueSelect
                  options={field.enumOptions}
                  value={field.defaultValue ?? ''}
                  onChange={v => onChange({ defaultValue: v || undefined })}
                  clearLabel="- None -"
                  disabled={!canEdit}
                />
              ) : (
                <input
                  type={fieldKind === 'number' || fieldKind === 'integer' ? 'number' : 'text'}
                  className={styles.fieldInput}
                  value={field.defaultValue ?? ''}
                  onChange={e => onChange({ defaultValue: e.target.value || undefined })}
                  step={fieldKind === 'integer' ? '1' : undefined}
                  placeholder="Pre-filled value shown to user"
                  disabled={!canEdit}
                />
              )}
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Description for AI</label>
              <input
                type="text"
                className={styles.fieldInput}
                value={field.description}
                onChange={e => onChange({ description: e.target.value })}
                placeholder="Brief description to help the AI understand this field"
                disabled={!canEdit}
              />
            </div>
          </div>

          <div className={styles.toggleRow}>
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={field.required}
                onChange={e => onChange({ required: e.target.checked })}
                disabled={!canEdit}
                className={styles.checkbox}
              />
              <span>Required field</span>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

function labelToName(label: string): string {
  return label
    .trim()
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}
